import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';
import { ApiError } from '~/utils/errors';

export interface Passkey {
    id: number;
    device_name: string | null;
    created_at: string;
    last_used_at: string | null;
}

export interface PasskeyRegistrationOptions {
    challenge: string;
    rp: {
        name: string;
        id: string;
    };
    user: {
        id: string;
        name: string;
        displayName: string;
    };
    pubKeyCredParams: Array<{ type: string; alg: number }>;
    authenticatorSelection: {
        authenticatorAttachment: string | null;
        userVerification: string;
        residentKey: string;
    };
    attestation: string;
    timeout: number;
    excludeCredentials: Array<{ id: string; type: string }>;
    _stateToken: string; // Encrypted state token for stateless verification
}

// Pure API service for passkey operations
export const passkeyService = {
    async getRegistrationOptions(email: string): Promise<PasskeyRegistrationOptions> {
        const nuxtApp = useNuxtApp();

        const body = await pbRequestRaw<PasskeyRegistrationOptions & { data?: PasskeyRegistrationOptions }>(
            nuxtApp.$api.routes.auth.passkeys.registerOptions(),
            {
                method: 'POST',
                body: { email },
                failure: 'Failed to get registration options',
            }
        );

        // The endpoint answers wrapped or unwrapped depending on the path taken through it.
        const options = body.data ?? body;

        // A credential cannot be minted without a challenge, so an answer without one is a
        // failure the browser API would otherwise report as an opaque DOM exception.
        if (!options.challenge) {
            throw new ApiError('Invalid registration options received from server', 500);
        }

        return options;
    },

    async registerPasskey(credential: unknown, stateToken: string, deviceName?: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        if (!credential) {
            throw new Error('Credential is required');
        }

        if (!stateToken) {
            throw new Error('State token is required');
        }

        await pbRequestVoid(nuxtApp.$api.routes.auth.passkeys.register(), {
            method: 'POST',
            body: {
                credential,
                stateToken,
                device_name: deviceName,
            },
            expect: 204,
            failure: 'Failed to register passkey',
        });
    },

    async getPasskeys(): Promise<Passkey[]> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<Passkey[]>(nuxtApp.$api.routes.me.passkeys.get(), {
            method: 'GET',
            failure: 'Failed to fetch passkeys',
        });
    },

    async deletePasskey(passkeyId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.me.passkeys.delete(passkeyId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to delete passkey',
        });
    },
};

